<script setup lang="ts">
import { T } from '@tolgee/vue'
import { computed, ref, watch } from 'vue'
import { type FortuneWheelPrizeV2 } from '@/types/shared/fortune-wheel'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import FeatherLargeImage from '@/assets/images/feather-large.png'
import TicketGifImage from '@/assets/images/gifs/ticket.gif'

const props = withDefaults(defineProps<{
	show: boolean
	prize: FortuneWheelPrizeV2
	appliedMultiplier?: number
	currencyImage?: string
	currencyLabel?: string
	imageClass?: string
	showCheckEntriesButton?: boolean
}>(), {
  appliedMultiplier: 1,
  imageClass: 'size-[226px] object-cover',
  showCheckEntriesButton: false,
})

defineEmits<{
	(e: 'hide'): void
	(e: 'onCheckEntries'): void
}>()

useWatchOverlays(computed(() => props.show))
const { topStyle } = useFullScreenDimensions()

const isClickable = ref(false)

watch(() => props.show, show => {
  if (show) {
    setTimeout(() => isClickable.value = true, 500)
  } else {
    isClickable.value = false
  }
}, { immediate: true })

// Get currency info from config if not provided via props
const currencyConfig = computed(() => {
  if (props.currencyImage && props.currencyLabel) {
    return {
      image: props.currencyImage,
      label: props.currencyLabel,
    }
  }
  return allSpecialCurrenciesConfig.value[props.prize.currency]
})

// Handle specific currency display overrides
const displayImage = computed(() => {
  if (props.currencyImage) return props.currencyImage

  // Special cases for specific currencies
  if (props.prize.currency === 'goldFeather') {
    return FeatherLargeImage
  }
  if (props.prize.currency === 'giveawayTicket') {
    return TicketGifImage
  }

  return currencyConfig.value?.image
})

const displayLabel = computed(() => {
  if (props.currencyLabel) return props.currencyLabel
  return currencyConfig.value?.label || props.prize.currency
})

const isTicketOverlay = computed(() => props.prize.currency === 'giveawayTicket')

const shouldShowCheckEntriesButton = computed(() => {
  // For tickets, show button by default unless explicitly disabled
  if (isTicketOverlay.value) {
    return props.showCheckEntriesButton !== false
  }
  return false
})

const imageClasses = computed(() => {
  // Special rotation for feathers
  if (props.prize.currency === 'goldFeather') {
    return 'size-[226px] rotate-[23deg]'
  }
  // Smaller size for tickets
  if (isTicketOverlay.value) {
    return 'size-[160px]'
  }
  return props.imageClass
})

</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.65] backdrop-blur-[4px] flex flex-col z-[800]"
      :class="isTicketOverlay && 'bg-dark-most-bottom-layer/[.95]'"
      @click="$emit('hide')"
    >
      <CrossCircleIcon
        class="w-6 h-6 min-w-[24px] absolute top-3 right-3 text-white/[87%]"
        :style="topStyle"
      />
      <div class="h-full w-full flex-1 flex flex-col gap-y-6 items-center justify-center relative">
        <div
          class="absolute w-[334px] h-[334px] rounded-full blur-[90px] -z-[1]"
          :class="isTicketOverlay ? 'bg-teal-primary blur-[150px]' : 'bg-[#FF00AB]'"
        />
        <img
          v-if="!isTicketOverlay"
          class="absolute w-[334px] h-[334px] -z-[1]"
          src="@/assets/images/goldwheel-overlay-bg.webp"
        >
        <div
          class="flex flex-col items-center"
          :class="isTicketOverlay && 'gap-y-3'"
        >
          <h1 class="text-white text-[36px] font-bold leading-[42px] text-center max-w-[316px]">
            <T
              v-if="isTicketOverlay"
              key-name="fortune_wheel.ticket_overlay.title"
              default-value="Congrats, you got 1 ticket!"
            />
            <T
              v-else
              key-name="fortune_wheel.feathers_overlay.title"
              default-value="Congratulations!"
            />
          </h1>
          <p
            v-if="isTicketOverlay"
            class="text-base text-light-white font-medium -tracking-[0.32px] whitespace-pre-line text-center"
          >
            <T
              key-name="fortune_wheel.ticket_overlay.description"
              :default-value="`You're in the draw for the Lucky Rush.\nGood luck!`"
            />
          </p>
          <div
            v-if="appliedMultiplier > 1 && isTicketOverlay"
            class="text-white/60 text-xs font-medium mt-2"
          >
            x{{ appliedMultiplier }} Multiplier applied
          </div>
        </div>

        <img
          v-if="displayImage"
          :src="displayImage"
          :class="imageClasses"
        >
        <div
          class="flex items-center flex-col"
          :class="isTicketOverlay && 'mt-6 gap-y-3'"
        >
          <template v-if="!isTicketOverlay">
            <div class="text-white/[.60] text-[24px] leading-8 font-medium">
              You won
            </div>
            <div class="text-[28px] leading-8 font-bold text-light-white -tracking-[0.56px]">
              {{ prize.amount }} {{ displayLabel }}
            </div>
            <div
              v-if="appliedMultiplier > 1"
              class="text-white/60 text-xs font-medium mt-2"
            >
              x{{ appliedMultiplier }} Multiplier applied
            </div>
          </template>
          <template v-else-if="shouldShowCheckEntriesButton">
            <div @click.stop.prevent="$emit('onCheckEntries')">
              <BaseButton
                class="min-w-[286px]"
              >
                <T
                  key-name="fortune_wheel.ticket_overlay.cta"
                  default-value="CHECK MY ENTRIES"
                />
              </BaseButton>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
	transition: opacity 0.5s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
	opacity: 0;
}
</style>
